import { Component, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { combineLatest, Subject } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";
import { HamptonComparable } from "src/app/core/valuation-process/_models/hampton-comparable.model";
import { selectValuationProcessConsiderationCriterion } from "src/app/core/valuation-process/_selectors/consideration-criterion.selector";
import { selectValuationProcessSelectedComparablesInHampton, selectValuationProcessTargetProperty, selectValuationProcessTargetPropertyUnitMAcronym } from "src/app/core/valuation-process/_selectors/valuation-process.selectors";

interface Comparable {
  refNum: string,
  buildingName: string,
  communityName: string
  size: number,
  price: number,
  bua: number,
  date: Date,
  evidenceType: string
}

@Component({
  selector: 'app-comparables-table',
  templateUrl: './comparables-table.component.html',
  styleUrls: ['./comparables-table.component.scss']
})
export class ComparablesTableComponent implements OnDestroy {
  defaultColumns = ['size', 'price', 'bua', 'date', 'evidence']
  columns = ['size', 'price', 'bua', 'date', 'evidence']
  unitMeasurement$ = this.store$.select(selectValuationProcessTargetPropertyUnitMAcronym)
  consideration$ = this.store$.select(selectValuationProcessConsiderationCriterion)
  currency$ = this.consideration$.pipe(
    map(consideration => consideration.countryCurrency)
  )

  viewModel$ = combineLatest([
    this.store$.select(selectValuationProcessSelectedComparablesInHampton),
    this.unitMeasurement$,
    this.currency$
  ]).pipe(
    map(([comparables, unitMeasurement, currency]) => {
      return {
        currency,
        unit: unitMeasurement,
        dataSource: comparables.map(comparable => this.mapComparablesToTableViewModel(comparable))
      }
    })
  )

  private _onDestroy$ = new Subject<void>()

  constructor(
    private store$: Store
  ) {
    this.store$.select(selectValuationProcessTargetProperty).pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(tp => {
      if (tp.propertySubType === 'Apartment' || tp.propertySubType === 'Office') {
        this.columns = ['building', ...this.defaultColumns]
      } else if (tp.propertySubType === 'House' || tp.propertySubType === 'Warehouse') {
        this.columns = ['community', ...this.defaultColumns]
      }
    })
  }

  ngOnDestroy(): void {
    this._onDestroy$.next()
    this._onDestroy$.complete()
  }

  private mapComparablesToTableViewModel(comparable: HamptonComparable): Comparable {
    return {
      refNum: comparable.refNum,
      buildingName: comparable.buildingName,
      communityName: comparable.communityName,
      size: comparable.size,
      price: comparable.price,
      bua: comparable.size == 0
        ? 0
        : comparable.price / comparable.size,
      date: comparable.sourceDate,
      evidenceType: comparable.sourceType 
    }
  }
} 